/**
 * Shared (multi-user) login.
 *
 * Many people can be logged in to this one account at the same time. The server gives every
 * login its own session id, so each person only sees what they submitted. Because the account
 * does not say who is filling the questionnaire, each submission asks for the person's own
 * name, phone, email and organisation (SubmitterDetailsModal), and the confirmation email goes
 * to that email.
 */
import { getUserData } from './auth';

export const SHARED_ACCOUNT_EMAIL = 'testuser@spplindia.org';
export const SHARED_ACCOUNT_PASSWORD = 'spplindia';

const STORAGE_KEY = 'oshmSubmitterDetails';

export const isSharedAccountUser = (user = getUserData()) =>
  Boolean(user && (user.isSharedAccount || String(user.email || '').toLowerCase() === SHARED_ACCOUNT_EMAIL));

// SubmitterDetailsModal registers itself here; it resolves with the entered details, or null on cancel.
let promptHandler = null;
export const registerSubmitterPrompt = (handler) => {
  promptHandler = handler;
  return () => {
    if (promptHandler === handler) promptHandler = null;
  };
};

// Details already confirmed for the submission in progress, so the API call does not ask twice.
let confirmedDetails = null;

const getSavedSubmitterDetails = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || null;
  } catch (e) {
    return null;
  }
};

/**
 * Show the details form (prefilled with what this tab entered last time).
 * @returns {Promise<{required: boolean, cancelled?: boolean, details?: object}>}
 */
export async function collectSubmitterDetails() {
  if (!isSharedAccountUser()) return { required: false };
  if (!promptHandler) {
    throw new Error('Submitter details form is not available. Please reload the page.');
  }
  const details = await promptHandler(getSavedSubmitterDetails());
  if (!details) return { required: true, cancelled: true };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(details));
  } catch (e) {
    // Prefill is a convenience only.
  }
  confirmedDetails = details;
  return { required: true, details };
}

/**
 * Used by the submit API calls: reuse details the page just collected, otherwise ask now.
 * @returns {Promise<{required: boolean, cancelled?: boolean, details?: object}>}
 */
export async function ensureSubmitterDetails() {
  if (!isSharedAccountUser()) return { required: false };
  if (confirmedDetails) {
    const details = confirmedDetails;
    confirmedDetails = null;
    return { required: true, details };
  }
  const result = await collectSubmitterDetails();
  confirmedDetails = null;
  return result;
}

export const SUBMISSION_CANCELLED_MESSAGE =
  'Submission cancelled. Your name, phone number, email and organisation are required to submit - your answers are still saved, so you can submit again.';
